import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JSZip from 'jszip';
import { AgentSetupModal } from './AgentSetupModal';
import { EditSkillModal } from './EditSkillModal';
import { Zap, Bot, Puzzle, Star, MoreVertical, History, Plus, ShieldCheck, Users, ArrowUpCircle, ExternalLink, Copy, Trash2, Folder, FolderMinus, FolderPlus, Pencil } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { formatRelativeDate } from '@/lib/skills-store';
import { canEditSkill } from '@/lib/skills-permissions';
import type { SkillAgent, SkillFolder } from '../../../../shared/types';

async function downloadAsSkillFile(skill: SkillAgent, prompt: string) {
  const nameSlug = skill.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/, '');
  const descIndented = skill.description.split('\n').map((l) => `  ${l}`).join('\n');
  const skillMd = `---\nname: ${nameSlug}\ndescription: >\n${descIndented}\n---\n\n${prompt}`;

  const zip = new JSZip();
  zip.file('SKILL.md', skillMd);
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${nameSlug}.skill`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface SkillAgentCardProps {
  skill: SkillAgent;
  currentUser: { email: string; isAdmin?: boolean; roles?: string[] } | null;
  folders?: SkillFolder[];
  activeFolderId?: string | null;
  onRemoveFromFolder?: () => void;
}

const TEAM_COLORS: Record<string, string> = {
  Marketing:  'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  Tech:       'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  Finance:    'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  Business:   'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  People:     'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
  IT:         'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
  Data:       'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400',
  Growth:     'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
};

export function SkillAgentCard({ skill, currentUser, folders = [], activeFolderId, onRemoveFromFolder }: SkillAgentCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canEdit = canEditSkill(currentUser, skill);
  const isTechOrAdmin = currentUser?.isAdmin || (currentUser?.roles ?? []).includes('tech');
  const utils = trpc.useUtils();

  const { data: coverImageData } = trpc.skills.getImageUrl.useQuery(
    { s3Key: skill.coverImageKey! },
    { enabled: !!skill.coverImageKey, staleTime: 55 * 60 * 1000 }
  );

  const toggleFavoriteMutation = trpc.skills.toggleFavorite.useMutation({
    onSuccess: () => utils.skills.getAll.invalidate(),
    onError: () => toast.error(t('skills.skillagentcard.no_se_pudo_actualizar_el_favorito')),
  });

  const proposeOfficialMutation = trpc.skills.proposeAsOfficial.useMutation({
    onSuccess: () => {
      utils.skills.getAll.invalidate();
      toast.success(t('skills.card.propose_success'));
    },
    onError: (err) => toast.error(`${t('skills.card.propose_error')}: ${err.message}`),
  });

  const deleteMutation = trpc.skills.delete.useMutation({
    onSuccess: () => { utils.skills.getAll.invalidate(); toast.success(t('skills.card.deleted_toast')); },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const incrementUseMutation = trpc.skills.incrementUseCount.useMutation();

  const addToFolderMutation = trpc.skills.addSkillToFolder.useMutation({
    onSuccess: () => { utils.skills.getFolders.invalidate(); toast.success(t('skills.card.added_to_folder')); },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const currentVersionData = skill.versions.find((v) => v.isCurrent);
  const iconColor = TEAM_COLORS[skill.team] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';

  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleUse = async () => {
    if (skill.type === 'agent') {
      setShowSetupModal(true);
      return;
    }
    const prompt = currentVersionData?.prompt ?? '';
    try {
      await downloadAsSkillFile(skill, prompt);
      incrementUseMutation.mutate({ skillId: skill.id });
      toast.success(t('skills.skillagentcard.archivo_skill_descargado_arrastralo_en_tu'));
    } catch {
      toast.error(t('skills.skillagentcard.no_se_pudo_generar_el_archivo'));
    }
  };

  const handleCopyPrompt = async () => {
    const prompt = currentVersionData?.prompt ?? '';
    try {
      await navigator.clipboard.writeText(prompt);
      incrementUseMutation.mutate({ skillId: skill.id });
      toast.success(t('skills.card.prompt_copied'));
    } catch {
      toast.error(t('skills.skillagentcard.no_se_pudo_copiar'));
    }
  };

  return (
    <Card
      className="relative flex flex-col gap-3 hover:shadow-md transition-shadow overflow-hidden cursor-grab active:cursor-grabbing"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('skillId', skill.id);
        e.dataTransfer.setData('fromFolderId', activeFolderId ?? '');
        e.dataTransfer.effectAllowed = 'move';
      }}
    >
      {/* Cover image */}
      {coverImageData?.url && (
        <div className="w-full h-24 mx-0 overflow-hidden">
          <img src={coverImageData.url} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className={cn('flex flex-col gap-3 p-5', coverImageData?.url && 'pt-3')}>
      {/* Top row: icon + version badge + favorite */}
      <div className="flex items-start justify-between">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg', iconColor)}>
          {skill.type === 'agent' ? <Bot className="h-5 w-5" />
            : skill.type === 'plugin' ? <Puzzle className="h-5 w-5" />
            : <Zap className="h-5 w-5" />}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs font-semibold">
            v{skill.currentVersion}
          </Badge>
          <button
            onClick={() => toggleFavoriteMutation.mutate({ skillId: skill.id })}
            disabled={toggleFavoriteMutation.isPending}
            className={cn(
              'rounded-full p-1 transition-colors hover:bg-accent',
              skill.isFavorite ? 'text-yellow-500' : 'text-muted-foreground'
            )}
            aria-label={skill.isFavorite ? t('skills.skillagentcard.quitar_de_favoritos') : t('skills.skillagentcard.agregar_a_favoritos')}
          >
            <Star className={cn('h-4 w-4', skill.isFavorite && 'fill-yellow-500')} />
          </button>
        </div>
      </div>

      {/* Name + type + source */}
      <div>
        <p className="font-semibold leading-tight">{skill.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          <p className="text-xs text-muted-foreground capitalize">
            {skill.type === 'agent' ? t('skills.card.type_agent') : skill.type === 'plugin' ? t('skills.card.type_plugin') : t('skills.card.type_skill')}
          </p>
          {skill.source === 'official' ? (
            <span className="inline-flex items-center gap-0.5 text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400 rounded-full px-1.5 py-0.5">
              <ShieldCheck className="h-3 w-3" />
              {t('skills.source.official')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-0.5 text-xs font-medium text-violet-600 bg-violet-50 dark:bg-violet-900/30 dark:text-violet-400 rounded-full px-1.5 py-0.5">
              <Users className="h-3 w-3" />
              {t('skills.source.community')}
            </span>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
        {skill.description}
      </p>

      {/* Tags */}
      <div className="flex flex-wrap gap-1">
        {skill.tags.map((tag) => (
          <Badge key={tag} variant="secondary" className="text-xs">
            {tag}
          </Badge>
        ))}
      </div>

      {/* Author + date */}
      <p className="text-xs text-muted-foreground">
        por {skill.author} · {formatRelativeDate(skill.updatedAt)}
      </p>

      {/* Actions */}
      <div className="flex items-center justify-between pt-1 border-t">
        <Button size="sm" onClick={handleUse}>
          {t('skills.card.use_btn')}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={t('common.a11y.more_actions')} className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleCopyPrompt}>
              <Copy className="h-4 w-4 mr-2" />
              {t('skills.card.copy_prompt')}
            </DropdownMenuItem>
            {(activeFolderId && onRemoveFromFolder) || folders.length > 0 ? (
              <>
                <DropdownMenuSeparator />
                {activeFolderId && onRemoveFromFolder ? (
                  <DropdownMenuItem onClick={onRemoveFromFolder}>
                    <FolderMinus className="h-4 w-4 mr-2 text-muted-foreground" />
                    {t('skills.card.remove_from_folder')}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <FolderPlus className="h-4 w-4 mr-2 text-muted-foreground" />
                      {t('skills.card.add_to_folder')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {folders.map((folder) => {
                        const inFolder = folder.skillIds.includes(skill.id);
                        return (
                          <DropdownMenuItem
                            key={folder.id}
                            disabled={inFolder}
                            onClick={() => addToFolderMutation.mutate({ folderId: folder.id, skillId: skill.id })}
                          >
                            <Folder className="h-4 w-4 mr-2" />
                            {folder.name}
                            {inFolder && <span className="ml-auto text-xs text-muted-foreground">✓</span>}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )}
              </>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate(`/skills-agentes/${skill.id}/history`)}>
              <History className="h-4 w-4 mr-2" />
              {t('skills.card.view_history')}
            </DropdownMenuItem>
            {canEdit && (
              <>
                <DropdownMenuItem onClick={() => setShowEditModal(true)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  {t('skills.skillagentcard.editar_skill')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/skills-agentes/${skill.id}/new-version`)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('skills.card.new_version')}
                </DropdownMenuItem>
              </>
            )}
            {canEdit && skill.source === 'community' && !skill.prUrl && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => proposeOfficialMutation.mutate({ skillId: skill.id })}
                  disabled={proposeOfficialMutation.isPending}
                >
                  <ArrowUpCircle className="h-4 w-4 mr-2 text-blue-500" />
                  <span className="text-blue-600 dark:text-blue-400 font-medium">{t('skills.card.propose_official')}</span>
                </DropdownMenuItem>
              </>
            )}
            {skill.prUrl && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href={skill.prUrl} target="_blank" rel="noreferrer" className="flex items-center">
                    <ExternalLink className="h-4 w-4 mr-2 text-muted-foreground" />
                    {skill.prStatus === 'open' ? t('skills.card.view_pr_open') : skill.prStatus === 'merged' ? t('skills.card.view_pr_merged') : t('skills.card.view_pr_closed')}
                  </a>
                </DropdownMenuItem>
              </>
            )}
            {isTechOrAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => { if (confirm(t('skills.card.delete_confirm'))) deleteMutation.mutate({ skillId: skill.id }); }}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('skills.card.delete')}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      </div>
      {showSetupModal && (
        <AgentSetupModal
          skill={skill}
          open={showSetupModal}
          onClose={() => setShowSetupModal(false)}
          onUseCount={() => incrementUseMutation.mutate({ skillId: skill.id })}
        />
      )}
      {showEditModal && (
        <EditSkillModal
          skill={skill}
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </Card>
  );
}
